import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube, ArrowRight, Heart } from "lucide-react";
import { useContent } from "@/lib/content-context";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";

export default function Footer() {
    const { content } = useContent();
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const f = content?.footer || {};
    const links = content?.hero?.nav_links || [];

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
            toast.error("Please enter a valid email");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/newsletter", { email });
            toast.success("Welcome to the hideout. Watch your inbox.");
            setEmail("");
        } catch (err) {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer className="relative bg-coffee text-cream overflow-hidden" data-testid="site-footer">
            {/* Coffee steam SVG divider */}
            <div className="coffee-divider w-full">
                <svg viewBox="0 0 1440 80" className="w-full h-12 md:h-20" preserveAspectRatio="none">
                    <path
                        d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40"
                        fill="none"
                        strokeWidth="1.5"
                        opacity="0.9"
                    />
                    <path
                        d="M0,60 C240,30 480,80 720,50 C960,20 1200,70 1440,40"
                        fill="none"
                        strokeWidth="1"
                        opacity="0.45"
                    />
                </svg>
            </div>

            {/* Coffee ring watermark */}
            <div
                aria-hidden
                className="absolute -right-32 -bottom-24 w-[460px] h-[460px] rounded-full border-[14px] border-gold/15 opacity-50 pointer-events-none"
            />
            <div
                aria-hidden
                className="absolute right-40 -bottom-10 w-[160px] h-[160px] rounded-full border-[6px] border-gold/10 opacity-40 pointer-events-none"
            />

            <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 pt-12 pb-10 grid grid-cols-1 md:grid-cols-12 gap-12">
                {/* Brand */}
                <div className="md:col-span-4">
                    {f.logo_url || content?.brand?.logo_url ? (
                        <div
                            aria-label="Cafe Hideout"
                            className="w-[120px] h-[120px] md:w-[140px] md:h-[140px]"
                            style={{
                                backgroundColor: "#f5e6c8",
                                WebkitMaskImage: `url(${fileUrl(content?.brand?.logo_url)})`,
                                maskImage: `url(${fileUrl(content?.brand?.logo_url)})`,
                                WebkitMaskRepeat: "no-repeat",
                                maskRepeat: "no-repeat",
                                WebkitMaskPosition: "left center",
                                maskPosition: "left center",
                                WebkitMaskSize: "contain",
                                maskSize: "contain",
                            }}
                        />
                    ) : (
                        <>
                            <div className="font-editorial font-bold text-4xl md:text-5xl text-cream leading-none">CAFE</div>
                            <div className="font-editorial font-bold text-4xl md:text-5xl text-cream leading-none -mt-1">Hideout</div>
                        </>
                    )}
                    <p className="font-body text-sm text-cream/70 mt-6 max-w-xs leading-relaxed">
                        {f.tagline || "A modern Indian cafe. Coffee with roots."}
                    </p>
                    <div className="flex gap-4 mt-8">
                        <a href={f.social?.instagram || "#"} aria-label="instagram" className="text-gold hover:text-cream transition-colors" data-testid="footer-instagram">
                            <Instagram size={18} strokeWidth={1.2} />
                        </a>
                        <a href={f.social?.linkedin || "#"} aria-label="linkedin" className="text-gold hover:text-cream transition-colors" data-testid="footer-linkedin">
                            <Linkedin size={18} strokeWidth={1.2} />
                        </a>
                        <a href={f.social?.youtube || "#"} aria-label="youtube" className="text-gold hover:text-cream transition-colors" data-testid="footer-youtube">
                            <Youtube size={18} strokeWidth={1.2} />
                        </a>
                    </div>
                </div>

                {/* Navigate */}
                <div className="md:col-span-2">
                    <h4 className="tracked text-gold/80 text-[10px] mb-5">Navigate</h4>
                    <ul className="space-y-3">
                        {links.map((l) => (
                            <li key={l.href}>
                                <Link
                                    to={l.href}
                                    className="tracked text-[11px] text-cream hover:text-gold transition-colors duration-300"
                                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Reach Us */}
                <div className="md:col-span-3">
                    <h4 className="tracked text-gold/80 text-[10px] mb-5">Reach Us</h4>
                    <ul className="space-y-4 text-sm text-cream/80">
                        <li>
                            <a href={`mailto:${f.reach_us?.email}`} className="hover:text-gold transition-colors" data-testid="footer-email">
                                {f.reach_us?.email}
                            </a>
                        </li>
                        {f.reach_us?.phone && (
                            <li>
                                <a href={`tel:${(f.reach_us.phone || '').replace(/\s+/g, '')}`} className="hover:text-gold transition-colors" data-testid="footer-phone">
                                    {f.reach_us.phone}
                                </a>
                            </li>
                        )}
                        <li className="pt-1">
                            <Link
                                to={f.reach_us?.partner_link || "/partner"}
                                data-testid="footer-partner-link"
                                className="inline-block tracked text-gold text-[10px] px-4 py-2 border border-gold/40 bg-cream/5 backdrop-blur-sm hover:bg-gold hover:text-coffee transition-colors duration-300"
                            >
                                {f.reach_us?.partner_text || "Partner with Us"} →
                            </Link>
                        </li>
                        <li className="pt-2">
                            <Link to="/careers" className="tracked text-[11px] text-cream hover:text-gold transition-colors duration-300" data-testid="footer-careers">
                                Careers
                            </Link>
                        </li>
                        <li>
                            <Link to="/terms" className="tracked text-[11px] text-cream hover:text-gold transition-colors duration-300" data-testid="footer-terms">
                                Terms &amp; Conditions
                            </Link>
                        </li>
                        <li>
                            <Link to="/privacy" className="tracked text-[11px] text-cream hover:text-gold transition-colors duration-300" data-testid="footer-privacy">
                                Privacy Policy
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* Newsletter */}
                <div className="md:col-span-3">
                    <h4 className="tracked text-gold/80 text-[10px] mb-5">{f.newsletter?.label || "Get Our Stories."}</h4>
                    <form onSubmit={submit} className="relative" data-testid="newsletter-form">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={f.newsletter?.placeholder || "your@email.com"}
                            data-testid="newsletter-email"
                            className="w-full bg-transparent border-b border-cream/40 focus:border-gold text-cream text-sm py-3 pr-10 outline-none placeholder:text-cream/40 transition-colors"
                        />
                        <button
                            type="submit"
                            disabled={submitting}
                            aria-label="subscribe"
                            data-testid="newsletter-submit"
                            className="absolute right-0 bottom-3 text-gold hover:text-cream transition-colors disabled:opacity-50"
                        >
                            <ArrowRight size={18} strokeWidth={1.4} />
                        </button>
                    </form>
                </div>
            </div>

            <div className="relative border-t border-cream/10 mt-4">
                <div className="max-w-[1480px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between items-center gap-3">
                    <div className="font-mono text-[10px] text-cream/50">
                        © {new Date().getFullYear()} Cafe Hideout. All rights reserved.
                    </div>
                    <div className="font-mono text-[10px] text-cream/50 flex items-center gap-1.5">
                        Made with <Heart size={11} className="fill-gold text-gold" /> in India
                    </div>
                </div>
            </div>
        </footer>
    );
}
